import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { CalendarDays, Users, Wrench, Mail, UserCog, LogOut, Menu, X, ShieldCheck, BarChart3 } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { useNoIndex } from '../ProtectedRoute';
import { Button } from '../../components/ui/button';

const navItems = [
  { to: '/admin', end: true, icon: CalendarDays, label: 'Agenda', testid: 'nav-agenda' },
  { to: '/admin/interventions', icon: Wrench, label: "Interventions", testid: 'nav-interventions' },
  { to: '/admin/clients', icon: Users, label: 'Clients', testid: 'nav-clients' },
  { to: '/admin/stats', icon: BarChart3, label: 'Statistiques', testid: 'nav-stats' },
  { to: '/admin/demandes-site', icon: Mail, label: 'Demandes site web', testid: 'nav-site-requests' },
  { to: '/admin/compte', icon: UserCog, label: 'Mon compte', testid: 'nav-account' },
];

const AdminLayout = () => {
  useNoIndex();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/connexion', { replace: true }); };

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-2 px-5 py-6 border-b border-neutral-800">
        <div className="w-9 h-9 rounded-lg bg-red-700 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-white font-bold leading-tight">EMS Plateforme</p>
          <p className="text-neutral-500 text-xs">Administration</p>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to} to={item.to} end={item.end} onClick={() => setOpen(false)}
            data-testid={item.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? 'bg-red-700 text-white' : 'text-neutral-400 hover:text-white hover:bg-neutral-800'
              }`
            }
          >
            <item.icon className="w-5 h-5 shrink-0" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-neutral-800">
        <div className="px-3 py-2 mb-2">
          <p className="text-white text-sm font-medium truncate">{user?.name}</p>
          <p className="text-neutral-500 text-xs truncate">{user?.email}</p>
        </div>
        <button onClick={handleLogout} data-testid="logout-button"
          className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors">
          <LogOut className="w-5 h-5" /> Déconnexion
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-neutral-100 flex">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-neutral-950 fixed inset-y-0 left-0 z-30">
        <SidebarContent />
      </aside>

      {/* Sidebar mobile */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="relative flex flex-col w-64 bg-neutral-950">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-neutral-950 text-white flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-red-600" />
            <span className="font-bold">EMS Plateforme</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" className="text-white">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
